import React, { useEffect, useRef } from "react";
import { FaEye, FaBullseye, FaRocket, FaHandsHelping } from "react-icons/fa";

const VisionMissionSection = () => {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10" ref={sectionRef}>
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-green-600 via-blue-500 to-green-600 bg-clip-text text-transparent animate-gradient">
              Vision & Mission
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Shaping future leaders through quality education and values
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div
            ref={(el) => (cardRefs.current[0] = el)}
            className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-2 hover:shadow-2xl rounded-2xl shadow-lg overflow-hidden border-t-4 border-green-500 bg-gradient-to-br from-green-50 via-white to-emerald-50"
          >
            <div className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <FaEye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Inspired by the vision of Dr. A.P.J. Abdul Kalam, we strive to achieve <strong className="text-green-600">100% literacy</strong> by providing a transformative platform. Our goal is to empower youngsters to reach glorious heights across diverse fields, shaping them into tomorrow’s entrepreneurs and leaders who excel in their domains.
              </p>
              <div className="mt-6 flex items-center gap-2 text-green-600">
                <FaRocket />
                <span className="text-sm font-medium">Empower. Excel. Lead.</span>
              </div>
            </div>
          </div>
          <div
            ref={(el) => (cardRefs.current[1] = el)}
            className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-2 hover:shadow-2xl rounded-2xl shadow-lg overflow-hidden border-t-4 border-blue-500 bg-gradient-to-br from-blue-50 via-white to-sky-50"
          >
            <div className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-600 to-sky-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <FaBullseye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                At Innovative, we are committed to grooming leaders who are not only thorough professionals but also compassionate human beings with strong values and excellent interpersonal skills. We aim to establish a <strong className="text-blue-600">world-class center for professional learning</strong> driven by professionalism, dynamism, innovation, and a robust value system.
              </p>
              <div className="mt-6 flex items-center gap-2 text-blue-600">
                <FaHandsHelping />
                <span className="text-sm font-medium">Integrity. Innovation. Impact.</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center animate-fade-in-delay">
          <div className="inline-block bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-sm border border-gray-100">
            <p className="text-gray-600 text-sm">
              <span className="font-semibold text-green-600">Inspiring minds,</span>{" "}
              <span className="font-semibold text-blue-600">shaping futures</span>
            </p>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        .animate-fade-in-delay {
          animation: fade-in 0.8s ease-out 0.3s forwards;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </section>
  );
};

export default VisionMissionSection;